
import * as THREE from 'three';

export interface Point2D {
    point: THREE.Vector2;
}

export class Node {
    public point: THREE.Vector2;
    public data: any;

    constructor(point: THREE.Vector2) {
        this.point = point.clone();
    }

}

/**
 * 頂点自身がひとつの座標点をもつ
 * - left には自身より小さい座標点
 * - right には自身より大きい座標点が含まれる
 * 座標点 Point2D の大小はは頂点の depth によって x,y 座標の大小で比較する
 */
export interface SearchNode extends Point2D {
    depth: number;
    left?: SearchNode;
    right?: SearchNode;
    userdata: any;
}

export class BuildTree {

    private nodes = [];

    public add(node: Node) {
        this.nodes.push(node);
    }

    private static getComparator(depth: number) {
        if (depth % 2 === 0) {
            return (a: Node, b: Node) => (a.point.x - b.point.x);
        } else {
            return (a: Node, b: Node) => (a.point.y - b.point.y);
        }
    }

    public buildTree(): SearchNode {
        return this.buildSubTree(this.nodes, 0);
    }

    private buildSubTree(nodes: Node[], depth: number): SearchNode {
        nodes.sort(BuildTree.getComparator(depth));
        const mid = Math.floor(nodes.length / 2);
        const p = nodes[mid];
        const n: SearchNode = {
            point: p.point,
            depth: depth,
            userdata: p.data
        };
        if (mid > 0) {
            n.left = this.buildSubTree(nodes.slice(0, mid), depth + 1);
        }
        if (mid + 1 < nodes.length) {
            n.right = this.buildSubTree(nodes.slice(mid + 1, nodes.length), depth + 1);
        }
        return n;
    }

}

export interface MeasuredPoint extends Point2D {
    dist: number; // 探索の中心点からの距離
    userdata: any;
}

export interface EuclideanSearch {
    node?: SearchNode; // 現在の探索対象
    query: THREE.Vector2; // 探索の中心点
    result: MeasuredPoint[]; // 近い順にソート
}

type lambda = (state: EuclideanSearch) => boolean;

export class KDTree {

    // 指定された座標から距離dist以内の点を探索
    public searchDist(tree: SearchNode, pos: THREE.Vector2, dist: number): MeasuredPoint[] | undefined {
        const euclid_search: EuclideanSearch = {
            node: tree,
            query: pos,
            result: []
        };
        const distance = (state: EuclideanSearch): boolean =>  state.result.slice(-1)[0]?.dist > dist;

        return this.searchEuclidean(euclid_search, distance);
    }

    // 指定された座標から近い距離の点をk個を探索
    public searchNum(tree: SearchNode, pos: THREE.Vector2, k: number) {
        const euclid_search: EuclideanSearch = {
            node: tree,
            query: pos,
            result: []
        };
        const k_neighborhood = (state: EuclideanSearch): boolean =>  state.result.length > k;

        return this.searchEuclidean(euclid_search, k_neighborhood);
    }

    // private search(tree: SearchNode, pos: THREE.Vector2, func: lambda) {
    //     const euclid_search: EuclideanSearch = {
    //         node: tree,
    //         query: pos,
    //         result: []
    //     };

    //     return this.searchEuclidean(euclid_search, func);
    // }

    private measure(pos: THREE.Vector2, node: SearchNode): number {
        return node.point.distanceTo(pos);
    }

    private searchEuclidean(state: EuclideanSearch, func: lambda): MeasuredPoint[] | undefined {
        const node : SearchNode = state.node;
        if (!node) return;
        const pos = state.query;
        const d = this.measure(pos, node); // ユークリッド距離を算出

        const measure : MeasuredPoint = {
            point: node.point,
            dist: d,
            userdata: node.userdata
        };

        state.result.push(measure);
        state.result.sort((a: MeasuredPoint, b: MeasuredPoint) => a.dist - b.dist);
        while (func(state)) state.result.pop();

        // 深さに応じてxまたはy座標で大小比較
        const compareX = (node.depth % 2 === 0);
        const threshold = compareX ? node.point.x : node.point.y;
        const value = compareX ? pos.x : pos.y;
        this.searchEuclidean({
            ...state,
            node: value < threshold ? node.left : node.right
        }, func);
        // ここでは最小値を直接計算せず下限で代用
        // (探索しなかった方に存在するかもしれない点と点posの距離の最小値)
        //   >= (点posと閾値thresholdが表す直線との距離)
        const dist2th = Math.abs(value - threshold);
        if (dist2th <= state.result[state.result.length - 1]?.dist) {
            this.searchEuclidean({
                ...state,
                node: value < threshold ? node.right : node.left
            }, func);
        }

        return state.result;
    }

}
